import type { Dispatch, SetStateAction } from "react";

import { AlertSeverity, type Nullable, YesNo } from "@/types/utils";

export interface QuestionWarning {
  helpHref?: string;
  message: string;
  severity: AlertSeverity;
}

export interface ReviewPageScoringGuidelines {
  high: string;
  low: string;
  medium: string;
}

export enum QuestionType {
  Email = "email",
  MultipleChoice = "multiple_choice",
  Name = "name",
  Number = "numerical",
  Phone = "phone",
  ShortAnswer = "text",
  SingleChoice = "single_choice",
  Voice = "voice",
  YesNo = "yes_no",
}

export type ChoiceBasicQuestionQuestionType =
  | QuestionType.SingleChoice
  | QuestionType.MultipleChoice;

export type SimpleBasicQuestionQuestionType =
  | QuestionType.Email
  | QuestionType.Name
  | QuestionType.Phone
  | QuestionType.ShortAnswer;

export enum NumericalRule {
  LessThan = "<",
  GreaterThan = ">",
  LessThanOrEqualTo = "<=",
  GreaterThanOrEqualTo = ">=",
  EqualTo = "=",
  NotEqualTo = "!=",
}

export enum ChoiceQualifyingExpression {
  Is = "[+]",
  IsNot = "[!]",
  Required = "[*]",
}

export interface Condition {
  optionText: string;
  qualifier?: ChoiceQualifyingExpression;
}

export interface ReviewPageQuestion {
  isSelected: boolean;
  question: string;
  questionId: string;
}

export interface ReviewPageCompetency {
  custom?: boolean;
  definition: string;
  displayName: string;
  hasCustomScoringGuidelines: boolean;
  id: string;
  isIncluded: boolean;
  name: string;
  questions: ReviewPageQuestion[];
  scoringGuidelines: ReviewPageScoringGuidelines;
}

// Avoid tons of indents on validationRules
// prettier-ignore
export interface ReviewPageBasicQuestionPayload<
  T extends QuestionType = QuestionType,
> {
  // Technically optional, but effectively required in virtually all cases. This
  // remains optional only for backwards compatability with old data.
  internalLabel?: string;
  isIncluded: boolean;
  isQualifying: boolean;
  questionText: string;
  questionType: T;
  showInSummary?: boolean;
  validationRules:Nullable<
      T extends ChoiceBasicQuestionQuestionType
    ? { choiceRules?: Condition[] }
    : T extends QuestionType.YesNo
    ? { yesNoRule?: YesNo }
    : T extends QuestionType.Number
    ? { numericalRule?: string }
    // Non-qualifying question types
    : never
  >;
}

export interface ReviewPageBasicQuestion<T extends QuestionType = QuestionType>
  extends ReviewPageBasicQuestionPayload<T> {
  id: string;
  isCustom: boolean;
  // rootId is undefined for identifying questions (name, email, phone). Those
  // questions are singular records in the database shared among all screeners.
  rootId?: string;
}

// Used for questions being edited or updated
export type ReviewPageDraftQuestion<T extends QuestionType = QuestionType> =
  | ReviewPageBasicQuestionPayload<T>
  | ReviewPageBasicQuestion<T>;

export type QuestionWithValidationRules = ReviewPageDraftQuestion<
  ChoiceBasicQuestionQuestionType | QuestionType.Number | QuestionType.YesNo
>;

export type QuestionStateSetter<T extends QuestionType = QuestionType> =
  Dispatch<SetStateAction<ReviewPageDraftQuestion<T>>>;
